import React from 'react';
import { getCompanyContext } from 'utils/setCompanyContext';
import { normalizeCompanyName } from 'utils/companyNormalizer';

const formatarMesLabel = (mesRef) => {
  const [ano, mes] = String(mesRef || '').split('-');
  const meses = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
  return `${meses[parseInt(mes) - 1] || 'Mes'}/${ano || 'Ano'}`;
};

const extrairFluxoRowsAndSeries = (lista = []) => {
  if (!Array.isArray(lista)) return { rows: [], series: [] };

  const rows = lista.filter((item) =>
    item &&
    typeof item === 'object' &&
    !Array.isArray(item) &&
    typeof item.mes === 'string' &&
    !Array.isArray(item.series)
  );

  const seriesWrapper = lista.find((item) =>
    item &&
    typeof item === 'object' &&
    !Array.isArray(item) &&
    Array.isArray(item.series)
  );

  return {
    rows,
    series: Array.isArray(seriesWrapper?.series) ? seriesWrapper.series : []
  };
};

const construirFluxoDinamico = (rows, series) => {
  if (!Array.isArray(rows) || rows.length === 0 || !Array.isArray(series) || series.length === 0) {
    return null;
  }

  const rowsOrdenadas = [...rows].sort((a, b) => {
    const dataA = new Date(`${a.mes}-01`);
    const dataB = new Date(`${b.mes}-01`);
    return dataA - dataB;
  });

  const palette = [
    '#22c55e',
    '#ef4444',
    '#a855f7',
    '#3b82f6',
    '#f59e0b',
    '#06b6d4'
  ];

  const datasets = series
    .filter((serie) => serie && typeof serie === 'object' && serie.key)
    .map((serie, index) => ({
      label: serie.label || serie.key,
      data: rowsOrdenadas.map((item) => parseFloat(item?.[serie.key]) || 0),
      borderColor: serie.color || palette[index % palette.length],
      backgroundColor: serie.color || palette[index % palette.length],
      fill: false,
      tension: 0.1
    }));

  if (datasets.length === 0) return null;

  return {
    labels: rowsOrdenadas.map((item) => formatarMesLabel(item.mes)),
    datasets,
    formatoMoeda: true
  };
};

const normalizarTipoFinanceiro = (valor = 'todos') =>
  String(valor || 'todos')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, '_');

const primeiraListaNaoVazia = (...listas) =>
  listas.find((lista) => Array.isArray(lista) && lista.length > 0) || [];

export const useFinanceiroData = (dadosFinanceiros, tipoFinanceiro = 'todos') => {
  const empresaAtual = getCompanyContext()?.companies?.[0]?.name || '';
  const isMovFit = normalizeCompanyName(empresaAtual) === 'mov fit';
  const tipoFinanceiroNormalizado = normalizarTipoFinanceiro(tipoFinanceiro);

  const labelEntradasBrutas = isMovFit ? 'Mensalidades (R$)' : 'Entradas Brutas (R$)';
  const labelEntradasLiquidas = isMovFit ? 'Mensalidades + Produtos (R$)' : 'Entradas Líquidas (R$)';

  // Verificar se são dados consolidados
  const isConsolidated = dadosFinanceiros?.isConsolidated || 
                        dadosFinanceiros?.total_empresas > 0 || 
                        dadosFinanceiros?.empresas?.length > 0;

  // Dados reais dos KPIs - ajustado para a estrutura da API
  const dadosReais = React.useMemo(() => {
    // Se são dados brutos (sem wrapper success/data)
    if (dadosFinanceiros?.kpis && !dadosFinanceiros?.data) {
      return dadosFinanceiros.kpis;
    }
    
    return isConsolidated 
      ? dadosFinanceiros?.kpis || {}
      : dadosFinanceiros?.data?.kpis || {};
  }, [isConsolidated, dadosFinanceiros]);
    
  const dadosFluxoCaixa = React.useMemo(() => {
    // Se são dados brutos (sem wrapper success/data)
    if (dadosFinanceiros?.fluxoCaixa && !dadosFinanceiros?.data) {
      return dadosFinanceiros.fluxoCaixa;
    }
    
    // Para dados consolidados, verificar múltiplas estruturas possíveis
    if (isConsolidated) {
      // Tentar diferentes campos que podem conter dados de fluxo de caixa
      return dadosFinanceiros?.fluxo_caixa || 
             dadosFinanceiros?.fluxoCaixa || 
             dadosFinanceiros?.data?.fluxoCaixa || 
             [];
    }
    
    return dadosFinanceiros?.data?.fluxoCaixa || [];
  }, [isConsolidated, dadosFinanceiros]);
    
  const dadosContasApagar = React.useMemo(() => {
    // Se são dados brutos (sem wrapper success/data)
    if (dadosFinanceiros?.contasApagarDia && !dadosFinanceiros?.data) {
      return dadosFinanceiros.contasApagarDia;
    }
    
    return isConsolidated 
      ? dadosFinanceiros?.contas_pagamento || []
      : dadosFinanceiros?.data?.contasApagarDia || [];
  }, [isConsolidated, dadosFinanceiros]);
    
    
  // Tentar diferentes campos possíveis para dados de cidade
  const dadosReceitaCidadeReal = React.useMemo(() => {
    // Se são dados brutos (sem wrapper success/data)
    if (!dadosFinanceiros?.data) {
      return primeiraListaNaoVazia(
        dadosFinanceiros?.dadosCidades,
        dadosFinanceiros?.dadosCidade
      );
    }
    
    if (isConsolidated) {
      return primeiraListaNaoVazia(
        dadosFinanceiros?.receita_cidade,
        dadosFinanceiros?.dadosCidades,
        dadosFinanceiros?.dadosCidade,
        dadosFinanceiros?.data?.dadosCidades,
        dadosFinanceiros?.data?.dadosCidade
      );
    }

    return primeiraListaNaoVazia(
      dadosFinanceiros?.data?.dadosCidades,
      dadosFinanceiros?.data?.dadosCidade,
      dadosFinanceiros?.data?.receitaCidade,
      dadosFinanceiros?.data?.receitaCidadeReal
    );
  }, [isConsolidated, dadosFinanceiros]);

  const dadosReceitaCidadeFiltrados = React.useMemo(() => {
    if (!Array.isArray(dadosReceitaCidadeReal) || dadosReceitaCidadeReal.length === 0) {
      return [];
    }

    const temTipoFinanceiroInformado = dadosReceitaCidadeReal.some((item) => item && item.tipo_financeiro);

    if (tipoFinanceiroNormalizado === 'todos' || !temTipoFinanceiroInformado) {
      return dadosReceitaCidadeReal;
    }

    return dadosReceitaCidadeReal.filter((item) => {
      const tipoItem = normalizarTipoFinanceiro(item?.tipo_financeiro);
      return tipoItem === tipoFinanceiroNormalizado;
    });
  }, [dadosReceitaCidadeReal, tipoFinanceiroNormalizado]);

  // Verificar se os dados estão prontos para renderização
  const dadosProntos = React.useMemo(() => {
    if (!dadosFinanceiros) {
      return false;
    }
    
    // Verificar se são dados consolidados
    if (isConsolidated) {
      return dadosFinanceiros.kpis && Object.keys(dadosFinanceiros.kpis).length > 0;
    }
    
    // Verificar se são dados individuais
    return dadosFinanceiros.data || 
           dadosFinanceiros.success ||
           (dadosFinanceiros.kpis && dadosFinanceiros.fluxoCaixa) ||
           dadosFinanceiros.kpis;
  }, [dadosFinanceiros, isConsolidated]);



  // Dados para gráfico de Pareto (Receita por Cidade) - Otimizado com useMemo
  const prepararDadosPareto = React.useMemo(() => {
    if (!dadosReceitaCidadeFiltrados || dadosReceitaCidadeFiltrados.length === 0) {
      return {
        labels: [],
        datasets: [],
        dadosProcessados: [],
        outrasCidades: []
      };
    }
    

    // Filtrar dados válidos - ajustar para dados consolidados
    const dadosValidos = dadosReceitaCidadeFiltrados.filter(item => 
      item && typeof item === 'object' && 
      item.cidade && 
      ((item.valor_total !== null && item.valor_total !== undefined) ||
       (item.receita !== null && item.receita !== undefined))
    );

    if (dadosValidos.length === 0) {
      return {
        labels: [],
        datasets: [],
        dadosProcessados: [],
        outrasCidades: []
      };
    }

    // Agregar dados por cidade antes de ordenar
    const dadosAgregadosPorCidade = {};
    
    dadosValidos.forEach(item => {
      const cidade = item.cidade || 'Cidade não informada';
      const valor = parseFloat(item.valor_total || item.receita) || 0;
      
      // Se a cidade já existe, somar os valores
      if (dadosAgregadosPorCidade[cidade]) {
        dadosAgregadosPorCidade[cidade] += valor;
      } else {
        dadosAgregadosPorCidade[cidade] = valor;
      }
    });

    // Converter objeto agregado em array e ordenar por valor (maior para menor)
    const dadosOrdenados = Object.entries(dadosAgregadosPorCidade)
      .map(([cidade, valor]) => ({
        cidade: cidade,
        valor: valor
      }))
      .sort((a, b) => b.valor - a.valor);

    // Separar as 5 primeiras cidades e agrupar o restante como "Outros"
    const top5Cidades = dadosOrdenados.slice(0, 5);
    const outrasCidades = dadosOrdenados.slice(5);
    
    // Calcular valor total das outras cidades
    const valorOutros = outrasCidades.reduce((sum, item) => sum + (item.valor || 0), 0);
    
    // Criar array final com top 5 + outros
    const dadosFinais = [
      ...top5Cidades,
      ...(valorOutros > 0 ? [{ cidade: 'Outros', valor: valorOutros }] : [])
    ];

    // Calcular valores acumulados e percentuais
    const valorTotal = dadosOrdenados.reduce((sum, item) => sum + (item.valor || 0), 0);
    let acumulado = 0;
    
    const dadosProcessados = dadosFinais.map((item, index) => {
      acumulado += item.valor || 0;
      const percentualAcumulado = valorTotal > 0 ? (acumulado / valorTotal) * 100 : 0;
      
      return {
        cidade: item.cidade,
        valor: item.valor || 0,
        percentualAcumulado: percentualAcumulado,
        posicao: index + 1,
        isOutros: item.cidade === 'Outros'
      };
    });

    // Encontrar quantas cidades representam 80% da receita
    const cidadesPara80Porcento = dadosProcessados.findIndex(item => (item.percentualAcumulado || 0) >= 80) + 1;

    return {
      labels: dadosProcessados.map(item => item.cidade),
      datasets: [
        {
          label: 'Receita por Cidade (R$)',
          data: dadosProcessados.map(item => item.valor || 0),
          backgroundColor: dadosProcessados.map((item, index) => {
            if (item.isOutros) {
              return 'rgba(156, 163, 175, 0.8)'; // Cinza para "Outros"
            }
            // Destacar as cidades que representam 80% da receita
            if (index < cidadesPara80Porcento) {
              return 'rgba(59, 130, 246, 0.8)'; // Azul mais intenso
            }
            return 'rgba(156, 163, 175, 0.6)'; // Cinza para as demais
          }),
          borderColor: dadosProcessados.map((item, index) => {
            if (item.isOutros) {
              return 'rgba(156, 163, 175, 1)';
            }
            if (index < cidadesPara80Porcento) {
              return 'rgba(59, 130, 246, 1)';
            }
            return 'rgba(156, 163, 175, 1)';
          }),
          borderWidth: 1,
          yAxisID: 'y'
        },
        {
          label: 'Receita Acumulada (%)',
          data: dadosProcessados.map(item => item.percentualAcumulado || 0),
          type: 'line',
          borderColor: 'rgba(239, 68, 68, 1)',
          backgroundColor: 'rgba(239, 68, 68, 0.1)',
          borderWidth: 3,
          fill: false,
          tension: 0.4,
          yAxisID: 'y1',
          pointBackgroundColor: 'rgba(239, 68, 68, 1)',
          pointBorderColor: '#fff',
          pointBorderWidth: 2,
          pointRadius: 4
        }
      ],
      dadosProcessados: dadosProcessados,
      outrasCidades: outrasCidades,
      formatoMoeda: true,
      mostrarLegenda: true
    };
  }, [dadosReceitaCidadeFiltrados]);

  // Dados para análise por cidade (DADOS REAIS) - Otimizado com useMemo
  const dadosAnaliseCidade = React.useMemo(() => {
    // Para dados consolidados, usar analise_cidade diretamente
    if (isConsolidated && tipoFinanceiroNormalizado === 'todos' && dadosFinanceiros?.analise_cidade) {
      return dadosFinanceiros.analise_cidade.map(item => ({
        cidade: item.cidade || 'N/A',
        contratos_distintos: item.contratos_distintos || item.contratos || 0,
        valor_total: item.valor_total || 0,
        valor_recebido: item.valor_recebido || 0,
        titulos_total: item.titulos_total || 0,
        titulos_recebidos: item.titulos_recebidos || 0,
        taxa_recebimento: item.taxa_recebimento || 0,
        participacao: item.participacao || 0,
        total_clientes: item.total_clientes || 0,
        ticket_medio: item.ticket_medio || 0,
        empresas: item.empresas || []
      }));
    }
    
    if (!dadosReceitaCidadeFiltrados || dadosReceitaCidadeFiltrados.length === 0) {
      return [];
    }

    // Agregar dados por cidade antes de processar
    const dadosAgregadosPorCidade = {};
    
    dadosReceitaCidadeFiltrados.forEach(item => {
      // Verificar se o item é válido
      if (!item || typeof item !== 'object' || !item.cidade) {
        return;
      }

      const cidade = item.cidade || 'Cidade não informada';
      const contratosDistintos = parseInt(item.contratos_distintos || item.contratos || 0) || 0;
      const valorTotal = parseFloat(item.valor_total || 0) || 0;
      const valorRecebido = parseFloat(item.valor_recebido || 0) || 0;
      const titulosTotal = parseInt(item.titulos_total || item.titulos_emitidos || 0) || 0;
      const titulosRecebidos = parseInt(item.titulos_recebidos || item.titulos_pagos || 0) || 0;
      
      // Se a cidade já existe, agregar os valores
      if (dadosAgregadosPorCidade[cidade]) {
        dadosAgregadosPorCidade[cidade].valor_total += valorTotal;
        dadosAgregadosPorCidade[cidade].valor_recebido += valorRecebido;
        dadosAgregadosPorCidade[cidade].contratos_distintos += contratosDistintos;
        dadosAgregadosPorCidade[cidade].titulos_total += titulosTotal;
        dadosAgregadosPorCidade[cidade].titulos_recebidos += titulosRecebidos;
      } else {
        // Criar nova entrada para a cidade
        dadosAgregadosPorCidade[cidade] = {
          cidade: cidade,
          contratos_distintos: contratosDistintos,
          valor_total: valorTotal,
          valor_recebido: valorRecebido,
          titulos_total: titulosTotal,
          titulos_recebidos: titulosRecebidos
        };
      }
    });

    // Converter objeto agregado em array e processar métricas
    const dadosProcessados = Object.values(dadosAgregadosPorCidade).map(item => {
      const valorTotal = item.valor_total || 0;
      const valorRecebido = item.valor_recebido || 0;
      const titulosTotal = item.titulos_total || 0;
      const titulosRecebidos = item.titulos_recebidos || 0;
      
      // Calcular taxa de recebimento baseada em TÍTULOS ao invés de valores
      // Se não houver dados de títulos, usar valores como fallback
      const taxaRecebimento = titulosTotal > 0 
        ? (titulosRecebidos / titulosTotal) * 100 
        : (valorTotal > 0 ? (valorRecebido / valorTotal) * 100 : 0);
      const ticketMedio = titulosRecebidos > 0
        ? valorRecebido / titulosRecebidos
        : (titulosTotal > 0 ? valorTotal / titulosTotal : 0);
      
      // Calcular participação (será calculada depois)
      const participacao = 0; // Será calculada abaixo
      
      return {
        cidade: item.cidade,
        contratos_distintos: item.contratos_distintos || 0,
        valor_total: valorTotal,
        valor_recebido: valorRecebido,
        titulos_total: titulosTotal,
        titulos_recebidos: titulosRecebidos,
        taxa_recebimento: taxaRecebimento,
        participacao: participacao,
        ticket_medio: ticketMedio
      };
    });

    // Verificar se há dados válidos
    if (dadosProcessados.length === 0) {
      return [];
    }

    // Calcular participação de cada cidade no total
    const valorTotalGeral = dadosProcessados.reduce((total, item) => total + (item.valor_total || 0), 0);
    dadosProcessados.forEach(item => {
      item.participacao = valorTotalGeral > 0 ? ((item.valor_total || 0) / valorTotalGeral) * 100 : 0;
    });

    // Ordenar por participação em ordem decrescente
    dadosProcessados.sort((a, b) => (b.participacao || 0) - (a.participacao || 0));

    return dadosProcessados;
  }, [dadosReceitaCidadeFiltrados, isConsolidated, dadosFinanceiros?.analise_cidade, tipoFinanceiroNormalizado]);

  // Dados para contas a pagar por dia (REAIS) - Otimizado com useMemo
  const dadosContasPagamento = React.useMemo(() => {
    if (!dadosContasApagar || dadosContasApagar.length === 0) {
      return {
        labels: [],
        datasets: []
      };
    }
    
    // Para dados consolidados, processar estrutura diferente
    if (isConsolidated) {
      // Dados consolidados têm estrutura: [{data: undefined, empresas: [...], valor: total}]
      const dadosProcessados = dadosContasApagar.map((item, index) => ({
        label: `Dia ${index + 1}`,
        valor: item.valor || 0
      }));
      
      return {
        labels: dadosProcessados.map(item => item.label),
        datasets: [{
          label: 'Contas a Pagar (R$)',
          data: dadosProcessados.map(item => item.valor),
          backgroundColor: dadosProcessados.map((item, index) => {
            const cores = [
              'rgba(239, 68, 68, 0.8)',   // Vermelho
              'rgba(59, 130, 246, 0.8)',  // Azul
              'rgba(16, 185, 129, 0.8)',  // Verde
              'rgba(245, 158, 11, 0.8)',  // Amarelo
              'rgba(139, 92, 246, 0.8)',  // Roxo
              'rgba(236, 72, 153, 0.8)',  // Rosa
              'rgba(6, 182, 212, 0.8)'    // Ciano
            ];
            return cores[index % cores.length];
          }),
          borderColor: dadosProcessados.map((item, index) => {
            const cores = [
              'rgba(239, 68, 68, 1)',
              'rgba(59, 130, 246, 1)',
              'rgba(16, 185, 129, 1)',
              'rgba(245, 158, 11, 1)',
              'rgba(139, 92, 246, 1)',
              'rgba(236, 72, 153, 1)',
              'rgba(6, 182, 212, 1)'
            ];
            return cores[index % cores.length];
          }),
          borderWidth: 1
        }]
      };
    }

    // Filtrar dados válidos para dados individuais
    const dadosValidos = dadosContasApagar.filter(item => 
      item && typeof item === 'object' && item.label && item.valor !== null && item.valor !== undefined
    );

    if (dadosValidos.length === 0) {
      return {
        labels: [],
        datasets: []
      };
    }

    return {
      labels: dadosValidos.map(item => item.label || 'Dia não informado'),
      datasets: [{
        label: 'Contas a Pagar (R$)',
        data: dadosValidos.map(item => parseFloat(item.valor) || 0),
        backgroundColor: dadosValidos.map((item, index) => {
          const cores = [
            'rgba(239, 68, 68, 0.8)',   // Vermelho
            'rgba(59, 130, 246, 0.8)',  // Azul
            'rgba(16, 185, 129, 0.8)',  // Verde
            'rgba(245, 158, 11, 0.8)',  // Amarelo
            'rgba(139, 92, 246, 0.8)',  // Roxo
            'rgba(236, 72, 153, 0.8)',  // Rosa
            'rgba(14, 165, 233, 0.8)'   // Azul claro
          ];
          return cores[index % cores.length];
        }),
        borderColor: dadosValidos.map((item, index) => {
          const cores = [
            'rgba(239, 68, 68, 1)',   // Vermelho
            'rgba(59, 130, 246, 1)',  // Azul
            'rgba(16, 185, 129, 1)',  // Verde
            'rgba(245, 158, 11, 1)',  // Amarelo
            'rgba(139, 92, 246, 1)',  // Roxo
            'rgba(236, 72, 153, 1)',  // Rosa
            'rgba(14, 165, 233, 1)'   // Azul claro
          ];
          return cores[index % cores.length];
        }),
        borderWidth: 2
      }],
      formatoMoeda: true,
      mostrarLegenda: false
    };
  }, [dadosContasApagar, isConsolidated]);

  // Dados para fluxo de caixa (REAIS) - Otimizado com useMemo
  const dadosFluxoCaixaGrafico = React.useMemo(() => {
    // Para dados consolidados, processar estrutura diferente
    if (isConsolidated) {
      const fluxoRaw = dadosFinanceiros?.fluxo_caixa_historico || [];
      const { rows: dadosAtuais, series } = extrairFluxoRowsAndSeries(fluxoRaw);

      const graficoDinamico = construirFluxoDinamico(dadosAtuais, series);
      if (graficoDinamico) return graficoDinamico;
      
      
      // Para consolidado, sempre usar dados atuais (fluxoCaixa) que têm valores corretos
      if (dadosAtuais.length > 0) {
        // Ordenar dados atuais por mês (mais antigo primeiro)
        const dadosOrdenados = [...dadosAtuais].sort((a, b) => {
          const dataA = new Date(a.mes + '-01');
          const dataB = new Date(b.mes + '-01');
          return dataA - dataB;
        });
        
        
        // Usar dados históricos reais
        const resultado = {
          labels: dadosOrdenados.map(item => formatarMesLabel(item.mes)),
          datasets: [
            {
              label: labelEntradasBrutas,
              data: dadosOrdenados.map(item => parseFloat(item.entradas_brutas) || 0),
              borderColor: 'rgb(0, 208, 31)',
              backgroundColor: 'rgba(16, 185, 129, 0.1)',
              fill: false,
              tension: 0.1
            },
            {
              label: labelEntradasLiquidas,
              data: dadosOrdenados.map(item => parseFloat(item.entradas_liquidas) || 0),
              borderColor: 'rgb(122, 255, 171)',
              backgroundColor: 'rgba(34, 197, 94, 0.1)',
              fill: false,
              tension: 0.1
            },
            {
              label: 'Saídas (R$)',
              data: dadosOrdenados.map(item => parseFloat(item.saidas) || 0),
              borderColor: 'rgba(239, 68, 68, 1)',
              backgroundColor: 'rgba(239, 68, 68, 0.1)',
              fill: false,
              tension: 0.1
            },
            {
              label: 'Saldo (R$)',
              data: dadosOrdenados.map(item => parseFloat(item.saldo) || 0),
              borderColor: 'rgba(59, 130, 246, 1)',
              backgroundColor: 'rgba(59, 130, 246, 0.1)',
              fill: false,
              tension: 0.1
            }
          ]
        };
        
        
        return resultado;
      } else if (dadosAtuais.length > 0) {
        // Se não há dados históricos válidos, usar dados atuais
        const resultado = {
          labels: dadosAtuais.map(item => formatarMesLabel(item.mes)),
          datasets: [
            {
              label: labelEntradasLiquidas,
              data: dadosAtuais.map(item => parseFloat(item.entradas_liquidas) || 0),
              borderColor: 'rgba(34, 197, 94, 1)',
              backgroundColor: 'rgba(34, 197, 94, 0.1)',
              fill: false,
              tension: 0.1
            },
            {
              label: labelEntradasBrutas,
              data: dadosAtuais.map(item => parseFloat(item.entradas_brutas) || 0),
              borderColor: 'rgba(16, 185, 129, 1)',
              backgroundColor: 'rgba(16, 185, 129, 0.1)',
              fill: false,
              tension: 0.1
            },
            {
              label: 'Saídas (R$)',
              data: dadosAtuais.map(item => parseFloat(item.saidas) || 0),
              borderColor: 'rgba(239, 68, 68, 1)',
              backgroundColor: 'rgba(239, 68, 68, 0.1)',
              fill: false,
              tension: 0.1
            },
            {
              label: 'Saldo (R$)',
              data: dadosAtuais.map(item => parseFloat(item.saldo) || 0),
              borderColor: 'rgba(59, 130, 246, 1)',
              backgroundColor: 'rgba(59, 130, 246, 0.1)',
              fill: false,
              tension: 0.1
            }
          ]
        };
        
        
        return resultado;
      } else {
        // Dados zerados se não houver dados
        return {
          labels: ['Jan/2025'],
          datasets: [
            {
              label: 'Entradas (R$)',
              data: [0],
              borderColor: 'rgba(34, 197, 94, 1)',
              backgroundColor: 'rgba(34, 197, 94, 0.1)',
              fill: false,
              tension: 0.1
            },
            {
              label: 'Saídas (R$)',
              data: [0],
              borderColor: 'rgba(239, 68, 68, 1)',
              backgroundColor: 'rgba(239, 68, 68, 0.1)',
              fill: false,
              tension: 0.1
            },
            {
              label: 'Saldo (R$)',
              data: [0],
              borderColor: 'rgba(59, 130, 246, 1)',
              backgroundColor: 'rgba(59, 130, 246, 0.1)',
              fill: false,
              tension: 0.1
            }
          ]
        };
      }
    }

    // Em modo individual, se houver series no fluxo atual (payload dinâmico da empresa),
    // prioriza esse bloco ao invés do fluxoCaixaHistorico legado.
    const fluxoAtualRaw = dadosFinanceiros?.fluxoCaixa || dadosFluxoCaixa || [];
    const { rows: rowsFluxoAtual, series: seriesFluxoAtual } = extrairFluxoRowsAndSeries(fluxoAtualRaw);
    const graficoAtualComSeries = construirFluxoDinamico(rowsFluxoAtual, seriesFluxoAtual);
    if (graficoAtualComSeries) return graficoAtualComSeries;

    // 🆕 CORRIGIDO: Para dados individuais, usar APENAS o histórico (sempre mês atual, ignora filtro)
    // O histórico já contém os últimos 12 meses + mês atual, sempre baseado no mês atual
    // NÃO usar dadosAtuais aqui pois vêm do período filtrado
    const dadosHistoricosRaw = dadosFinanceiros?.fluxoCaixaHistorico || [];
    const { rows: dadosHistoricos, series: seriesHistorico } = extrairFluxoRowsAndSeries(dadosHistoricosRaw);
    const graficoHistoricoDinamico = construirFluxoDinamico(dadosHistoricos, seriesHistorico);
    if (graficoHistoricoDinamico) return graficoHistoricoDinamico;
    
    // Se temos histórico, usar apenas ele (já contém tudo: últimos 12 meses + mês atual)
    if (dadosHistoricos.length > 0) {
      // Ordenar dados históricos por mês (mais antigo primeiro)
      const dadosOrdenados = [...dadosHistoricos].sort((a, b) => {
        const dataA = new Date(a.mes + '-01');
        const dataB = new Date(b.mes + '-01');
        return dataA - dataB;
      });
      
      return {
        labels: dadosOrdenados.map(item => formatarMesLabel(item.mes)),
        datasets: [
          {
            label: labelEntradasLiquidas,
            data: dadosOrdenados.map(item => parseFloat(item.entradas_liquidas || item.entradas_liquida) || 0),
            borderColor: 'rgba(34, 197, 94, 1)',
            backgroundColor: 'rgba(34, 197, 94, 0.1)',
            fill: false,
            tension: 0.1
          },
          {
            label: labelEntradasBrutas,
            data: dadosOrdenados.map(item => parseFloat(item.entradas_brutas || item.entradas_bruta) || 0),
            borderColor: 'rgba(16, 185, 129, 1)',
            backgroundColor: 'rgba(16, 185, 129, 0.1)',
            fill: false,
            tension: 0.1
          },
          {
            label: 'Saídas (R$)',
            data: dadosOrdenados.map(item => parseFloat(item.saidas || item.saida) || 0),
            borderColor: 'rgba(239, 68, 68, 1)',
            backgroundColor: 'rgba(239, 68, 68, 0.1)',
            fill: false,
            tension: 0.1
          },
          {
            label: 'Saldo (R$)',
            data: dadosOrdenados.map(item => parseFloat(item.saldo) || 0),
            borderColor: 'rgba(59, 130, 246, 1)',
            backgroundColor: 'rgba(59, 130, 246, 0.1)',
            fill: false,
            tension: 0.1
          }
        ],
        formatoMoeda: true
      };
    }
    
    // Fallback: se não houver histórico, tentar usar dados atuais (mas isso não deveria acontecer)
    // Nota: isso é apenas um fallback caso o histórico não esteja disponível
    const dadosAtuaisRaw = dadosFluxoCaixa || [];
    const { rows: dadosAtuais, series: seriesAtuais } = extrairFluxoRowsAndSeries(dadosAtuaisRaw);
    const graficoAtualDinamico = construirFluxoDinamico(dadosAtuais, seriesAtuais);
    if (graficoAtualDinamico) return graficoAtualDinamico;

    if (dadosAtuais.length > 0) {
      const dadosOrdenados = [...dadosAtuais].sort((a, b) => {
        const dataA = new Date((a.mes || '') + '-01');
        const dataB = new Date((b.mes || '') + '-01');
        return dataA - dataB;
      });
      
      return {
        labels: dadosOrdenados.map(item => formatarMesLabel(item.mes)),
        datasets: [
          {
            label: labelEntradasLiquidas,
            data: dadosOrdenados.map(item => parseFloat(item.entradas_liquidas || item.entradas_liquida) || 0),
            borderColor: 'rgba(34, 197, 94, 1)',
            backgroundColor: 'rgba(34, 197, 94, 0.1)',
            fill: false,
            tension: 0.1
          },
          {
            label: labelEntradasBrutas,
            data: dadosOrdenados.map(item => parseFloat(item.entradas_brutas || item.entradas_bruta) || 0),
            borderColor: 'rgba(16, 185, 129, 1)',
            backgroundColor: 'rgba(16, 185, 129, 0.1)',
            fill: false,
            tension: 0.1
          },
          {
            label: 'Saídas (R$)',
            data: dadosOrdenados.map(item => parseFloat(item.saidas || item.saida) || 0),
            borderColor: 'rgba(239, 68, 68, 1)',
            backgroundColor: 'rgba(239, 68, 68, 0.1)',
            fill: false,
            tension: 0.1
          },
          {
            label: 'Saldo (R$)',
            data: dadosOrdenados.map(item => parseFloat(item.saldo) || 0),
            borderColor: 'rgba(59, 130, 246, 1)',
            backgroundColor: 'rgba(59, 130, 246, 0.1)',
            fill: false,
            tension: 0.1
          }
        ],
        formatoMoeda: true
      };
    }

    // Fallback: dados zerados se não houver dados
    return {
      labels: [],
      datasets: []
    };
  }, [dadosFluxoCaixa, isConsolidated, dadosFinanceiros, labelEntradasBrutas, labelEntradasLiquidas]);

  // Dados para fluxo de caixa histórico (se disponível)
  
  // Usar dadosFluxoCaixaGrafico como fallback principal
  const dadosFluxoCaixaHistoricoGrafico = dadosFluxoCaixaGrafico;

  return {
    dadosReais,
    dadosProntos,
    prepararDadosPareto,
    dadosAnaliseCidade,
    dadosContasPagamento,
    dadosFluxoCaixaHistoricoGrafico,
    isConsolidated
  };
};
